import { Html } from "@react-three/drei";
import { useEffect, useState, useMemo, useCallback } from "react";
import { CanvasTexture, SRGBColorSpace } from "three";
import { useUserInterfaceStore } from "../../store";
import { textureCache } from "../../services/textureCache";
import { extractYouTubeVideoId, convertToEmbedUrl } from "../../utils/youtubeHelpers";
import useIsMobile from "../Hooks/useIsMobile";


function DecoratedIframe({ decor, edges = <></>, disableRaycast = false }) {
  const { width, height, option, type } = decor;
  const currentPlayerMode = useUserInterfaceStore((s) => s.playerMode);
  const isMobile = useIsMobile();
  const isEditing = currentPlayerMode === "editor";

  const [thumbnailTexture, setThumbnailTexture] = useState(null);
  const [iframeActivated, setIframeActivated] = useState(false);
  const handleClose = useCallback(() => setIframeActivated(false), []);

  const showMobileThumbnail = !isEditing && isMobile && type === "youtube" && !iframeActivated;

  // Ensure YouTube URLs use the embed format so the iframe doesn't
  // frame-bust and navigate the parent page.
  // Use youtube-nocookie.com to avoid Safari content blocker killing
  // DoubleClick ad scripts that the embed player depends on.
  const iframeSrc = useMemo(() => {
    if (!option || type !== "youtube") return option;
    let src = convertToEmbedUrl(option) || option;
    src = src
      .replace("www.youtube.com/embed/", "www.youtube-nocookie.com/embed/")
      .replace("youtube.com/embed/", "youtube-nocookie.com/embed/");
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}playsinline=1&rel=0`;
  }, [option, type]);

  const raycastProps = disableRaycast ? { raycast: () => null } : {};

  // Get thumbnail URL - same approach as RoomEditorTools
  const thumbnailUrl = useMemo(() => {
    if (type !== "youtube" || !option) return null;
    const videoId = extractYouTubeVideoId(option);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  }, [type, option]);

  useEffect(() => {
    if (!thumbnailUrl) {
      setThumbnailTexture(null);
      return;
    }

    let mounted = true;

    // Check cache first
    const cached = textureCache.get(thumbnailUrl);
    if (cached) {
      setThumbnailTexture(cached);
      return;
    }

    // Load texture
    textureCache.load(thumbnailUrl)
      .then((texture) => {
        if (mounted) {
          setThumbnailTexture(texture);
        }
      })
      .catch(() => {
        if (mounted) setThumbnailTexture(null);
      });

    return () => {
      mounted = false;
    };
  }, [thumbnailUrl]);

  // Reset activation when URL changes
  useEffect(() => { setIframeActivated(false); }, [option]);

  // Composite thumbnail + play button into a single texture for mobile
  const mobileThumbnailTexture = useMemo(() => {
    if (!thumbnailTexture || !thumbnailTexture.image) return null;
    const img = thumbnailTexture.image;
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");

    // Draw the thumbnail
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Semi-transparent dark scrim for contrast
    ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // YouTube play button \u2014 sized relative to canvas
    const btnW = canvas.width * 0.07;
    const btnH = btnW * (48 / 68);
    const btnX = (canvas.width - btnW) / 2;
    const btnY = (canvas.height - btnH) / 2;
    const r = btnH * 0.18;

    // Red rounded rect
    ctx.fillStyle = "rgba(255, 0, 0, 0.9)";
    ctx.beginPath();
    ctx.moveTo(btnX + r, btnY);
    ctx.lineTo(btnX + btnW - r, btnY);
    ctx.quadraticCurveTo(btnX + btnW, btnY, btnX + btnW, btnY + r);
    ctx.lineTo(btnX + btnW, btnY + btnH - r);
    ctx.quadraticCurveTo(btnX + btnW, btnY + btnH, btnX + btnW - r, btnY + btnH);
    ctx.lineTo(btnX + r, btnY + btnH);
    ctx.quadraticCurveTo(btnX, btnY + btnH, btnX, btnY + btnH - r);
    ctx.lineTo(btnX, btnY + r);
    ctx.quadraticCurveTo(btnX, btnY, btnX + r, btnY);
    ctx.fill();

    // White triangle
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.moveTo(btnX + btnW * 0.38, btnY + btnH * 0.25);
    ctx.lineTo(btnX + btnW * 0.38, btnY + btnH * 0.75);
    ctx.lineTo(btnX + btnW * 0.72, btnY + btnH * 0.5);
    ctx.closePath();
    ctx.fill();

    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    return tex;
  }, [thumbnailTexture]);

  // Dispose composited texture on change/unmount
  useEffect(() => {
    return () => {
      if (mobileThumbnailTexture) mobileThumbnailTexture.dispose();
    };
  }, [mobileThumbnailTexture]);

  // In editor mode for YouTube: render a simple mesh with thumbnail texture
  // This avoids Html component interference with raycasting
  if (isEditing && type === "youtube") {
    return (
      <mesh {...raycastProps}>
        <planeGeometry args={[width, height]} />
        {thumbnailTexture ? (
          <meshStandardMaterial
            map={thumbnailTexture}
            color="white"
            transparent={false}
            opacity={1.0}
            side={0}
          />
        ) : (
          <meshStandardMaterial color="#1a1a1a" side={0} />
        )}
        {edges}
      </mesh>
    );
  }

  // Mobile player mode for YouTube: show thumbnail with composited play button
  if (showMobileThumbnail) {
    return (
      <mesh
        onPointerDown={(e) => {
          e.stopPropagation();
          if (document.pointerLockElement) document.exitPointerLock();
          setIframeActivated(true);
        }}
      >
        <planeGeometry args={[width, height]} />
        {mobileThumbnailTexture ? (
          <meshBasicMaterial map={mobileThumbnailTexture} side={0} />
        ) : thumbnailTexture ? (
          <meshStandardMaterial
            map={thumbnailTexture}
            color="white"
            transparent={false}
            opacity={1.0}
            side={0}
          />
        ) : (
          <meshStandardMaterial color="#1a1a1a" side={0} />
        )}
        {edges}
      </mesh>
    );
  }

  // In editor mode for Spotify: render a simple colored placeholder
  // This avoids Html component interference with raycasting
  if (isEditing && type === "spotify") {
    return (
      <mesh {...raycastProps}>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial color="#1DB954" side={0} />
        {edges}
      </mesh>
    );
  }

  // In editor mode for Twitch: render a purple placeholder
  if (isEditing && type === "twitch") {
    return (
      <mesh {...raycastProps}>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial color="#9146FF" side={0} />
        {edges}
      </mesh>
    );
  }

  // Player mode: render the iframe
  // Don't render if option is missing
  if (!option) {
    return (
      <mesh {...raycastProps}>
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial color="#333" side={0} />
        {edges}
      </mesh>
    );
  }

  // Mobile: large CSS3D iframes crash iOS due to GPU compositing layer memory.
  // Only shrink decorations above the threshold; small ones stay full size.
  // Threshold tuned from testing: area 1119 works, 1718 is flaky, 2268 crashes.
  const MOBILE_MAX_AREA = 1200;
  const area = width * height;
  const needsShrink = isMobile && area > MOBILE_MAX_AREA;

  let pxScale = 40;
  let dFactor = 10;
  if (needsShrink) {
    const scale = Math.sqrt(MOBILE_MAX_AREA / area);
    pxScale = 40 * scale;
    dFactor = 10 / scale;
  }

  // When iframe is active and shrunk, show black backing at full size
  // so the wall/background doesn't show through around the smaller iframe.
  const showBlackBacking = isMobile && iframeActivated && needsShrink;

  return (
    <mesh {...raycastProps}>
      <planeGeometry args={[width, height]} />
      <meshBasicMaterial
        transparent={!showBlackBacking}
        opacity={showBlackBacking ? 1 : 0}
        color="black"
        side={0}
      />
      <Html
        key={`iframe-${option}`}
        transform={true}
        center
        zIndexRange={[100, 0]}
        distanceFactor={dFactor}
        onClick={() => {
          if (document.pointerLockElement) document.exitPointerLock();
        }}
      >
        <div style={{ position: 'relative', width: `${width * pxScale}px`, height: `${height * pxScale}px` }}>
          {iframeActivated && isMobile && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              style={{
                position: 'absolute',
                top: -32,
                right: 0,
                zIndex: 10,
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '4px 10px',
                fontSize: 14,
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              {'\u2715'}
            </button>
          )}
          <iframe
            src={iframeSrc}
            width="100%"
            height="100%"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            style={{
              pointerEvents: 'auto',
              border: 'none',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
            }}
            title="interactive"
          />
        </div>
      </Html>
      {edges}
    </mesh>
  );
}

export default DecoratedIframe;
